import {action, classNames, Fragment, observable, observer, React, useAsObservableSource, useCallback} from '@api'
import {EditableText, Icon, Menu, MenuDivider, Spinner} from '@blueprintjs/core'
import {Placement, Popover2, Tooltip2} from '@api'
import {ContextMenuItem, ContextMenuItemProps, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {globalStores, icons} from '@stores'
import {PrimaryIconButton} from 'centric-ui-components'
import * as css from './FlexLayoutToolbarMenu.css'

/**
 * Deprecated: {@see ReportHeaderChip}
 */
export const FlexLayoutToolbarMenu = observer<{ className?: string, placement?: Placement }>(function FlexLayoutToolbarDropdownButton({className, placement}) {
  const {flexLayout, catalog} = globalStores

  // return null
  //if (!flexLayout) return null

  return (
    <Popover2
      interactionKind='hover'
      minimal
      placement={placement ?? 'bottom-start'}
      className={className}
      content={<Menu className={css.menu}>
        {flexLayout.isMaximized &&
		 <Fragment>
			 <RestoreTabsFromMaximize/>
			 <MaximizeNextTab/>
			 <MaximizePreviousTab/>
			 {/*<MenuDivider/>*/}
		 </Fragment>}
        {/*<LayoutPresetsMenuItem/>*/}
      </Menu>}>
      {/*<Tooltip2 content='Custom Layout' placement='right'>*/}
      <PrimaryIconButton icon={icons.flexLayout}/>
      {/*</Tooltip2>*/}

    </Popover2>
  )
})

const FlexLayoutDockToolbarItems = observer(function FlexLayoutDockToolbarItems() {
  const {flexLayout: {isShowingPDFOptionsTab, isShowingSettingsTab, isShowingCatalogItemDetailsTab, isShowingLibraries, isShowingFilters, toggleKnownTab}, catalog} = globalStores

  return (
    <ContextMenuItem icon='learning' text='Dock Items'>
      <ContextMenuItem rightIcon={icons.flexLayout} text='Layout' active={isShowingSettingsTab} onClick={() => toggleKnownTab('flexLayoutSettings')}/>
      <ContextMenuItem rightIcon={icons.catalogLibrary} text='Libraries' active={isShowingLibraries} onClick={() => toggleKnownTab('libraries')}/>
      <ContextMenuItem rightIcon={icons.filter} text='Filters' active={isShowingFilters} onClick={() => toggleKnownTab('filters')}/>
      <ContextMenuItem rightIcon={icons.inlineDetails} text='Catalog Item Details' active={isShowingCatalogItemDetailsTab} onClick={() => catalog.settings.detailLoc = isShowingCatalogItemDetailsTab ? 'modal' : 'sidebar'}/>
      <ContextMenuItem rightIcon={icons.pdf} text='PDF Options' active={isShowingPDFOptionsTab} onClick={() => toggleKnownTab('pdfOptions')}/>
    </ContextMenuItem>
  )
})

export const FlexLayoutToolbarItems = observer(function FlexLayoutToolbarItems() {
  return (<div className={css.toolbarItems}>
    <FlexLayoutToolbarMenu className={css.layoutDropdownButton}/>
  </div>)
})

export const FlexLayoutMaximizedToolbarItems = observer<{ className?: string }>(function FlexLayoutMaximizedToolbarItems({className}) {
  const {flexLayout: {maximizedTab}} = useStores()
  return <div className={classNames(className, css.maximizedTabToolbarItems, !maximizedTab && css.hide)}>
	  <RestoreFlexLayoutFromMaximizedButton/>
  </div>
})

const RestoreFlexLayoutFromMaximizedButton = observer(function RestoreFlexLayoutFromMaximizedButton() {
  const {flexLayout: {isMaximized, loading, lastMaximizedTab, toggleMaximize}} = useStores()

  if (!isMaximized && !lastMaximizedTab) return null

  return (
    <PrimaryIconButton tooltip={loading ? null : isMaximized ? 'Restore Tabs' : `Maximize '${lastMaximizedTab.displayName}'`} tooltipPosition='bottom'
                       wrapperClassName={css.restoreTabsButton}
                       icon={<Icon icon={loading ? <Spinner/> : 'minimize'}/>} onClick={() => toggleMaximize()}/>
  )
})

const RestoreTabsFromMaximize = observer(function RestoreTabsFromMaximize() {
  const {flexLayout: {toggleMaximize, tabs}} = useStores()

  return (<Fragment>
    <ContextMenuItem icon='minimize' text={`Restore Tabs (${tabs.length})`} onClick={() => toggleMaximize()}/>
  </Fragment>)
})

const MaximizeNextTab = observer(function MaximizeNextTab() {
  const {flexLayout: {maximizeTab, nextTab: tab, tabs}} = useStores()

  if (tabs.length <= 1) return null
  return (<Fragment>
    <ContextMenuItem icon='arrow-right' disabled={!tab} text={`Next Tab${tab ? ` - ${tab.name} (${tabs.findIndex(t => t.id === tab.id) + 1} of ${tabs.length})` : ''}`} onClick={() => maximizeTab(tab.id)}/>
  </Fragment>)
})

const MaximizePreviousTab = observer(function MaximizePreviousTab() {
  const {flexLayout: {maximizeTab, previousTab: tab, tabs}} = useStores()

  if (tabs.length <= 1) return null
  return (<Fragment>
    <ContextMenuItem icon='arrow-left' disabled={!tab} text={`Previous Tab${tab ? ` - ${tab.name} (${tabs.findIndex(t => t.id === tab.id) + 1} of ${tabs.length})` : ''}`} onClick={() => maximizeTab(tab.id)}/>
  </Fragment>)
})

export const FlexToolboxMenuItem = observer<ContextMenuItemProps>(function FlexToolboxMenuItem({children, ...props}) {
  return <ContextMenuItem icon='wrench' text='Toolbox' {...props} >
    <FlexToolboxMenuItems/>
  </ContextMenuItem>
})

export const FlexToolboxMenuItems = observer(function FlexToolboxMenuItems() {
  const {flexLayout: {create: {newCatalogTab}}} = useStores()
  return (<Fragment>
    <ContextMenuItem icon={icons.card} text='Card View' onClick={() => newCatalogTab({view: 'card'})}/>
    <ContextMenuItem icon={icons.table} text='Table View' onClick={() => newCatalogTab({view: 'table'})}/>
    <ContextMenuItem icon={icons.pdf} text='PDF View' onClick={() => newCatalogTab({view: 'pdf'})}/>

    {/*<C8FlexLayoutCreateMenuItems dividerBefore/>*/}
  </Fragment>)
})

export const C8FlexLayoutCreateMenuItems = observer<ContextMenuItemProps>(function C8FlexLayoutCreateMenuItems(props) {
  const {config: {config: {centricURL}}, deployments: {deploymentConfig: {hideC8Button}}, flexLayout: {create: {newC8Tab}}} = useStores()

  if (!centricURL || hideC8Button) return null

  return <ContextMenuItem {...props} icon={icons.c8} text='Centric8'>
    <ContextMenuItem icon={icons.c8} text='Dashboard' onClick={() => newC8Tab({name: 'dashboard'})}/>
    <ContextMenuItem icon={icons.inlineDetails} text='Details Item' onClick={() => newC8Tab({name: 'detailsItem'})}/>
  </ContextMenuItem>
})

export const FlexLayoutDockMenuItem = observer<ContextMenuItemProps>(function FlexLayoutDockMenuItem({children, ...props}) {
  return <ContextMenuItem icon='column-layout' text='Sidebars' {...props} >
    <FlexLayoutDockMenuItems/>
    {children}
  </ContextMenuItem>
})

export const FlexLayoutDockMenuItems = observer(function FlexLayoutDockMenuItems() {
  const {flexLayout: {isShowingLeftBorder, isShowingBottomBorder, isShowingRightBorder, isShowingTopBorder, isShowingNoBorders, isShowingAllBorders, toggleBorder}} = useStores()

  return (<Fragment>
    {/*<ContextMenuItem active={isShowingNoBorders} text='None' rightIcon='clean' onClick={() => toggleBorder(isShowingNoBorders ? 'all' : 'none')}/>*/}
    {/*<ContextMenuItem active={isShowingAllBorders} text='All' rightIcon='tick' onClick={() => toggleBorder(isShowingAllBorders ? 'none' : 'all')}/>*/}
    {/*<MenuDivider/>*/}
    {/*/!*<ContextMenuItem active={isShowingTopBorder} text='Top' rightIcon='add-row-top' onClick={() => toggleBorder('top')}/>*!/*/}
    <ContextMenuItem active={isShowingLeftBorder} text='Left' rightIcon='add-column-left' onClick={() => toggleBorder('left')}/>
    <ContextMenuItem active={isShowingRightBorder} text='Right' rightIcon='add-column-right' onClick={() => toggleBorder('right')}/>
    {/*<ContextMenuItem active={isShowingBottomBorder} text='Bottom' rightIcon='add-row-bottom' onClick={() => toggleBorder('bottom')}/>*/}

  </Fragment>)
})
/*

const LayoutPresetsMenuItem = observer(function LayoutPresetsMenuItem() {

  return (
    <Fragment>
      <FlexLayoutPresetMenuItems/>
    </Fragment>
  )
  // <ContextMenuItem icon='prescription' text='Presets'>// </ContextMenuItem>
})

export const FlexLayoutPresetMenuItems = observer<{ inPanel?: boolean }>(function FlexLayoutPresetMenuItems({inPanel}) {
  const {flexLayout: {presetsStore: {onReportSaveAs_Start, onReportSaveOutstandingChanges, onResetToEmptyLayout}}} = useStores()
    //presetMenuItems = presets.map((p, i) => <FlexLayoutEsPresetMenuItem preset={p} key={i} inPanel={inPanel}/>)

  return (<Fragment>
    <ContextMenuItem shouldDismissPopover={false} text={`New Report`} icon={'document'} onClick={onResetToEmptyLayout}/>
    <ContextMenuItem shouldDismissPopover={false} text={`Open Recent...`} icon={'folder-open'}>
      <RecentlyOpenedReportMenuItems/>
    </ContextMenuItem>
    <MenuDivider />
    <ContextMenuItem disabled shouldDismissPopover={false} text={`Save Report`} icon={'floppy-disk'} onClick={onReportSaveOutstandingChanges}/>
    <ContextMenuItem disabled shouldDismissPopover={false} text={`Save Report As...`} icon={'cloud-upload'} onClick={onReportSaveAs_Start}/>
  </Fragment>)
})

class FlexLayoutEsPresetMenuItemStore extends VipLocalStore {
  constructor(s, public props: { preset: ICatalogPresetES }) {
    super(s)
  }

  @observable isRenaming = false
  @observable tempName

  @action deletePreset = async () => {
    const {flexLayout, stores: {presets}, props: {preset}} = this

    flexLayout.presets = flexLayout.presets.filter(p => p != preset)
    await this.stores.presets.deletePreset(this.props.preset)
  }

  @action toggleRename = () => {
    this.isRenaming = !this.isRenaming
    if (this.isRenaming) {
      this.tempName = this.props.preset.label
    }
  }

  @action onChangeName = (value: string) => {
    this.tempName = value
  }

  @action onConfirmChangeName = () => {
    this.props.preset.label = this.tempName
    this.stores.presets.savePreset(this.props.preset)
    this.isRenaming = false
  }

  @action onCancelChangeName = () => {
    this.isRenaming = false
    this.tempName = null
  }

  @action onMouseEnter = () => {
    this.flexLayout.previewPreset = this.props.preset
  }

  @action onMouseLeave = () => {
    if (this.flexLayout.previewPreset === this.props.preset) {
      this.flexLayout.previewPreset = null
    }
  }
}

const FlexLayoutEsPresetMenuItem = observer<{ inPanel?: boolean, preset: ICatalogPresetES }>(function FlexLayoutEsPresetMenuItem({inPanel, preset}) {
  const {_id, label} = preset,
    obsProps = useAsObservableSource({preset}),
    {flexLayout: {loadPreset, settings}, currentUser} = useStores(),
    store = useVipLocalStore(s => new FlexLayoutEsPresetMenuItemStore(s, obsProps)),
    {toggleRename, deletePreset, onConfirmChangeName, onCancelChangeName, isRenaming, onChangeName, tempName, onMouseEnter, onMouseLeave} = store,
    menuItem = <ContextMenuItem
      //onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      shouldDismissPopover={false} active={settings.preset === _id} text={label} onClick={() => loadPreset(_id)}/>

  if (inPanel) {
    return <div className={css.presetRow}>
      {isRenaming ? <EditableText value={tempName} onConfirm={onConfirmChangeName} onCancel={onCancelChangeName} onChange={onChangeName}/> : menuItem}
      <PrimaryIconButton wrapperClassName={css.showOnHover} icon='edit' tooltipPosition='bottom' tooltip={'Rename'} onClick={toggleRename}/>
      {preset.creator === currentUser.info?.id && <PrimaryIconButton wrapperClassName={css.showOnHover} icon='cross' tooltipPosition='bottom' tooltip={'Delete'} onClick={deletePreset}/>}
    </div>
  }

  return menuItem
})

export const FlexPresetsMenuItem = observer<ContextMenuItemProps>(function LayoutMenuItem({children, ...props}) {
  if (!DEBUG) return null
  return (<Fragment>
    <ContextMenuItem icon='page-layout' text='Presets' {...props}>
      <FlexLayoutPresetMenuItems/>
      {children}
    </ContextMenuItem>
  </Fragment>)
})
*/

const RecentlyOpenedReportMenuItems = observer(function RecentlyOpenedReportMenuItems() {
  const {strings, users: {user}, catalog: {idFor}, reports: {recentReports, currentReport}} = useStores()
  if (!user?.id) return null

  return (<Fragment>
    {recentReports.map((r) => <ContextMenuItem active={r.isCurrentReport} text={r.displayName} key={r.id} onClick={() => r.navigateTo()} />)}
  </Fragment>)
})


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/FlexLayoutToolbarMenu.tsx